import { authStorage } from './authStorage';

let onUnauthorized: (() => void) | null = null;
let refreshingPromise: Promise<boolean> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

// Minimalny odstęp między proaktywnymi odświeżeniami. Zabezpiecza przed pętlą "refresh co sekundę",
// gdy access token ma krótki TTL albo zegar urządzenia odbiega (refreshInMs wychodzi <= 0).
const MIN_REFRESH_DELAY_MS = 5_000;

// Jedyne wejście do odświeżania tokena. Wszystkie ścieżki (proaktywny timer ORAZ obsługa 401)
// muszą iść przez to, żeby przy przeładowaniu strony nigdy nie poleciały dwa równoległe żądania
// /auth/refresh z tym samym refresh tokenem — przy rotacji tokenów przegrany wyścig kończył się
// nieuprawnionym wylogowaniem (objawiało się głównie na Safari z powodu kolejności fetchy).
function getRefreshPromise(): Promise<boolean> {
  if (!refreshingPromise) {
    refreshingPromise = tryRefreshToken().finally(() => { refreshingPromise = null; });
  }
  return refreshingPromise;
}

function decodeTokenClaims(token: string): { exp: number | null; iat: number | null } {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return { exp: payload.exp ?? null, iat: payload.iat ?? null };
  } catch {
    return { exp: null, iat: null };
  }
}

async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = authStorage.getRefreshToken();
  if (!refreshToken) return false;

  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) return false;

    const data = await res.json();
    if (!data.result?.access_token) return false;

    // Backend nie zawsze rotuje refresh token — gdy go nie zwróci, zachowujemy dotychczasowy
    // zamiast wpisywać do localStorage `undefined`/pustą wartość (to psuło kolejne odświeżenia).
    const newRefresh = data.result.refresh_token || authStorage.getRefreshToken();
    if (!newRefresh) return false;

    authStorage.setTokens(data.result.access_token, newRefresh);
    return true;
  } catch {
    return false;
  }
}

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

export function scheduleTokenRefresh(onLogout: () => void): void {
  if (refreshTimer) clearTimeout(refreshTimer);

  const token = authStorage.getAccessToken();
  if (!token) return;

  const { exp, iat } = decodeTokenClaims(token);
  if (!exp) return;

  // Odstęp liczymy z ŻYWOTNOŚCI tokena (exp - iat), a nie z exp - Date.now(). exp i iat pochodzą
  // z tego samego zegara serwera, więc ich różnica jest odporna na rozjechany zegar urządzenia —
  // to był powód pętli "refresh co sekundę" na Safari/iOS: przy zegarze spieszącym się o >14 min
  // exp*1000 - Date.now() wychodziło <= 0 i token był odświeżany w kółko. Mierzymy odstęp względem
  // TERAZ (czas odebrania tokena), używając zegara klienta tylko do mierzenia upływu czasu.
  // Gdy brak iat (nietypowy token) — fallback do starego, bezwzględnego rachunku.
  const lifetimeMs = iat ? (exp - iat) * 1000 : (exp * 1000 - Date.now());

  // Zawsze przez setTimeout z dolnym limitem — nawet dla krótkiego/wygasłego tokena czekamy
  // MIN_REFRESH_DELAY_MS zamiast odświeżać natychmiast w pętli. Wygasły token i tak obsłuży
  // ścieżka 401 w apiService (przez ten sam getRefreshPromise), więc nic tu nie tracimy.
  const refreshInMs = Math.max(lifetimeMs - 30_000, MIN_REFRESH_DELAY_MS);

  refreshTimer = setTimeout(async () => {
    const success = await getRefreshPromise();
    if (success) scheduleTokenRefresh(onLogout);
    else onLogout();
  }, refreshInMs);
}

export function stopTokenRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

export const apiUpload = async (endpoint: string, formData: FormData) => {
  const accessToken = authStorage.getAccessToken();
  const url = `/api${endpoint}`;
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let response = await fetch(url, { method: 'POST', headers, body: formData });

  if (response.status === 401) {
    const refreshed = await getRefreshPromise();
    if (refreshed) {
      const newToken = authStorage.getAccessToken();
      response = await fetch(url, {
        method: 'POST',
        headers: { ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}) },
        body: formData,
      });
    } else {
      onUnauthorized?.();
      throw new Error('Sesja wygasła. Zaloguj się ponownie.');
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Błąd HTTP: ${response.status}`);
  }
  const data = await response.json();
  return data.result || data;
};

export const apiService = async (endpoint: string, method: string = 'GET', body: any = null) => {
  const accessToken = authStorage.getAccessToken();

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  const url = `/api${endpoint}`;
  let response = await fetch(url, config);

  // Fallback: token wygasł — spróbuj odświeżyć i ponów zapytanie
  if (response.status === 401) {
    const refreshed = await getRefreshPromise();

    if (refreshed) {
      const newToken = authStorage.getAccessToken();
      response = await fetch(url, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${newToken}`,
        },
      });
    } else {
      onUnauthorized?.();
      throw new Error('Sesja wygasła. Zaloguj się ponownie.');
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Błąd HTTP: ${response.status}`);
  }

  const data = await response.json();
  return data.result || data;
};
